import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BASE_API_URL } from "./config";
import { a, useAppColorMode } from "./appColors";
import { hasSession } from "./auth/session";
import { logout } from "./auth/logout";
import { clearPendingJoin, pendingJoin, rememberPendingJoin } from "./auth/pendingJoin";
import { AuthNotice, AuthPage, primaryButtonStyle, secondaryButtonStyle } from "./auth/AuthShell";

const API = `${BASE_API_URL}/api/v1/course-invites/token`;

/**
 * Where a class invite lands: an email invite's link, or a class's join link.
 *
 * Signed in as a student, it adds them and takes them into the class.
 * Signed out, it holds the invite while they log in or sign up; both of
 * those come back here when they're done (see completeLogin), and this page
 * finishes the join.
 */
const JoinClassPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isDark, toggle } = useAppColorMode();
  const [invite, setInvite] = useState(null);
  // "loading" | "invalid" | "signedOut" | "joining" | "refused" | "failed"
  const [stage, setStage] = useState("loading");
  const [message, setMessage] = useState(null);
  const started = useRef(false);

  const join = async (preview) => {
    setStage("joining");
    try {
      const { data } = await axios.post(`${API}/${token}/accept`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      clearPendingJoin();
      navigate("/classroom/student/class", {
        replace: true,
        state: { courseGuid: data.course_guid, classroomName: data.course_name, userType: "STUDENT" },
      });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        // Wrong kind of account, or not the address the invite went to. Keep
        // the invite so it's still there after they switch accounts.
        rememberPendingJoin({ token, courseName: preview.course_name, email: preview.email });
        setMessage(detail);
        setStage("refused");
      } else if (status === 404 || status === 410) {
        clearPendingJoin();
        setMessage(detail);
        setStage("invalid");
      } else {
        setStage("failed");
      }
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    axios
      .get(`${API}/${token}`)
      .then(({ data }) => {
        setInvite(data);
        if (hasSession()) {
          join(data);
        } else {
          rememberPendingJoin({ token, courseName: data.course_name, email: data.email });
          setStage("signedOut");
        }
      })
      .catch((err) => {
        // Used already, but signed in: it may be theirs (accepted when they
        // logged in), and accepting again lets them through or says why not.
        if (err.response?.status === 410 && hasSession()) {
          join({ course_name: pendingJoin()?.courseName, email: pendingJoin()?.email });
          return;
        }
        clearPendingJoin();
        setMessage(err.response?.data?.detail || null);
        setStage("invalid");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const switchAccount = () => logout({ navigate });

  const heading = (
    <>
      <Text fontSize="13px" fontWeight="500" color={a.textSub} textTransform="uppercase" letterSpacing="0.06em">
        Space invite
      </Text>
      <Text fontSize="24px" fontWeight="600" color={a.text} mt="6px" lineHeight="1.25">
        {invite ? `Join ${invite.course_name}` : "Join a space"}
      </Text>
      {invite && (
        <Text fontSize="15px" fontWeight="300" color={a.textSub} mt="6px">
          {invite.instructor_name ? `${invite.instructor_name} has invited you` : "You've been invited"} to
          {" "}their class on Stande
          {invite.subject_name ? ` (${invite.subject_name})` : ""}.
        </Text>
      )}
    </>
  );

  let body = null;
  if (stage === "loading" || stage === "joining") {
    body = (
      <Flex align="center" gap="10px" mt="28px" color={a.textSub}>
        <Spinner size="sm" color={a.accent} />
        <Text fontSize="14px">{stage === "joining" ? "Adding you to the space…" : "Opening your invite…"}</Text>
      </Flex>
    );
  } else if (stage === "invalid") {
    body = (
      <Box mt="24px">
        <AuthNotice tone="warn" title="This invite can't be used">
          {message || "It may have been cancelled, or the link reset. Ask your teacher for a new one."}
        </AuthNotice>
        <Link to="/login">
          <Button sx={secondaryButtonStyle} mt="20px">Go to Stande</Button>
        </Link>
      </Box>
    );
  } else if (stage === "signedOut") {
    body = (
      <Box mt="28px">
        {invite?.email && (
          <Text fontSize="14px" color={a.textSub} mb="16px">
            Use <Text as="span" fontWeight="600" color={a.text}>{invite.email}</Text> — the invite is for that address.
          </Text>
        )}
        <Link to="/create-account?as=student">
          <Button sx={primaryButtonStyle}>Create a student account</Button>
        </Link>
        <Link to="/login?as=student">
          <Button sx={secondaryButtonStyle} mt="12px">I already have an account — log in</Button>
        </Link>
        <Text fontSize="13px" fontWeight="300" color={a.textMuted} mt="16px">
          You'll be added to the space as soon as you've logged in.
        </Text>
      </Box>
    );
  } else if (stage === "refused") {
    body = (
      <Box mt="24px">
        <AuthNotice tone="warn" title="You can't join with this account">
          {message}
        </AuthNotice>
        <Button sx={primaryButtonStyle} mt="20px" onClick={switchAccount}>
          Log out and use another account
        </Button>
        <Link to="/classrooms">
          <Button sx={secondaryButtonStyle} mt="12px">Not now</Button>
        </Link>
      </Box>
    );
  } else if (stage === "failed") {
    body = (
      <Box mt="24px">
        <AuthNotice title="Something went wrong">We couldn't add you to the class just now.</AuthNotice>
        <Button sx={primaryButtonStyle} mt="20px" onClick={() => join(invite)}>
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <AuthPage isDark={isDark} toggle={toggle}>
      <Box textAlign="left">
        {heading}
        {body}
      </Box>
    </AuthPage>
  );
};

export default JoinClassPage;
